import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, Loader2, Search, Paperclip, Inbox, CalendarClock, ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';
import { ORDEM_PADRAO, TAMANHOS_PAGINA, proximaOrdem, ordenarLinhas, paginar } from './mapa-caixa-tabela';

// =====================================================================
// FINANCEIRO — Mapa de Caixa: a CONSULTA (a rede, para o escritório).
//
// Lista os mapas lançados num período, já com a Quebra de Caixa e a Diferença
// GPRS que o servidor derivou. O recorte por loja é do backend: o gerente só vê
// a própria loja mesmo que forje o filtro.
// =====================================================================

const API = '/api/financeiro/mapa-caixa';
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const brl = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dataBr = (iso) => (iso ? String(iso).split('-').reverse().join('/') : '—');
const hojeIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const primeiroDoMes = () => `${hojeIso().slice(0, 7)}-01`;

// Célula de valor com sinal colorido (Quebra / Diferença GPRS).
const celSinal = (v) => {
  if (v === null || v === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
  const cor = v > 0 ? '#047857' : v < 0 ? '#b91c1c' : '#334155';
  return <span style={{ color: cor, fontWeight: 700 }}>{v > 0 ? '+' : ''}{brl(v)}</span>;
};

const th = { textAlign: 'right', padding: '10px 12px', fontSize: 12, fontWeight: 800, color: COR_FORTE, whiteSpace: 'nowrap', borderBottom: `2px solid ${COR_BORDA}` };
const thL = { ...th, textAlign: 'left' };
const td = { textAlign: 'right', padding: '9px 12px', fontSize: 13, color: '#0f172a', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid #f1f5f9' };
const tdL = { ...td, textAlign: 'left' };

// Cabeçalho clicável: a seta mostra a ordem ativa (↑ crescente, ↓ decrescente) e fica
// apagada (⇅) nas colunas que não estão ordenando. `aria-sort` serve o leitor de tela.
const CabOrdenavel = ({ chave, ordem, onOrdenar, esquerda, children }) => {
  const ativa = ordem.chave === chave;
  const Icone = !ativa ? ChevronsUpDown : ordem.sentido === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th style={esquerda ? thL : th} aria-sort={!ativa ? 'none' : ordem.sentido === 'asc' ? 'ascending' : 'descending'}>
      <button type="button" onClick={() => onOrdenar(chave)}
        title={ativa ? (ordem.sentido === 'asc' ? 'Crescente — clique para inverter' : 'Decrescente — clique para inverter') : 'Clique para ordenar'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', whiteSpace: 'nowrap' }}>
        {children}
        <Icone size={13} style={{ opacity: ativa ? 1 : 0.4 }} />
      </button>
    </th>
  );
};

const MapaCaixaConsulta = () => {
  const [ordem, setOrdem] = useState(ORDEM_PADRAO);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHOS_PAGINA[0]);
  const [de, setDe] = useState(primeiroDoMes());
  const [ate, setAte] = useState(hojeIso());
  const [loja, setLoja] = useState('');
  const [lojas, setLojas] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  // Correção de data (só Admin/Financeiro — quem diz é o `/opcoes`, não o front).
  const [podeCorrigirData, setPodeCorrigirData] = useState(false);
  const [corrigindo, setCorrigindo] = useState(null); // { loja, data } da linha em edição
  const [novaData, setNovaData] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erroCorrecao, setErroCorrecao] = useState('');

  // Bananas do PDV por lançamento: a linha aberta e o que já foi buscado.
  // ⚠️ Chave = loja|data do mapa. Se a data for corrigida a chave muda e as bananas
  //    são buscadas de novo na data nova (o servidor deriva, não há cópia gravada).
  const [abertas, setAbertas] = useState({});
  const [bananas, setBananas] = useState({}); // chave -> { carregando, erro, lista }

  const alternarBananas = async (l) => {
    const chave = `${l.loja}|${l.data}`;
    const abrir = !abertas[chave];
    setAbertas((a) => ({ ...a, [chave]: abrir }));
    if (!abrir || bananas[chave]?.lista) return;
    setBananas((b) => ({ ...b, [chave]: { carregando: true } }));
    try {
      const params = new URLSearchParams({ loja: l.loja, data: l.data });
      const r = await fetch(`${API}/bananas?${params.toString()}`, { headers: auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível buscar as bananas.');
      setBananas((b) => ({ ...b, [chave]: { lista: corpo.bananas, parcial: !!corpo.parcial } }));
    } catch (ex) {
      setBananas((b) => ({ ...b, [chave]: { erro: ex.message } }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/opcoes`, { headers: auth() });
        if (r.ok) setPodeCorrigirData(!!(await r.json()).podeCorrigirData);
      } catch { /* sem o botão: a consulta segue só de leitura */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/lojas`, { headers: auth() });
        if (r.ok) setLojas(await r.json());
      } catch { /* o filtro de loja é opcional; sem a lista, consulta a rede toda */ }
    })();
  }, []);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params = new URLSearchParams({ de, ate });
      if (loja) params.set('loja', loja);
      const r = await fetch(`${API}/consulta?${params.toString()}`, { headers: auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível carregar a consulta.');
      setDados(corpo);
      setBananas({}); // o PDV pode ter fechado o dia desde a última busca
      setPagina(1); // resultado novo: volta ao começo (a ordem escolhida é mantida)
    } catch (ex) {
      setErro(ex.message);
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, [de, ate, loja]);

  const ordenar = (chave) => { setOrdem((o) => proximaOrdem(o, chave)); setPagina(1); };
  const trocarTamanho = (n) => { setTamanho(n); setPagina(1); };

  // Ordena e pagina só o que já foi carregado (ver aviso de "truncado" acima da tabela).
  const pag = useMemo(
    () => paginar(ordenarLinhas(dados?.linhas, ordem), pagina, tamanho),
    [dados, ordem, pagina, tamanho]
  );

  const abrirCorrecao = (l) => { setCorrigindo({ loja: l.loja, data: l.data }); setNovaData(''); setErroCorrecao(''); };

  const salvarCorrecao = async () => {
    setSalvando(true);
    setErroCorrecao('');
    try {
      const r = await fetch(`${API}/dia/data`, {
        method: 'PATCH',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ loja: corrigindo.loja, dataAtual: corrigindo.data, novaData }),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível corrigir a data.');
      setCorrigindo(null);
      buscar();
    } catch (ex) {
      setErroCorrecao(ex.message);
    } finally {
      setSalvando(false);
    }
  };

  // Busca uma vez ao abrir (o período padrão é o mês corrente). Depois, só no botão.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { buscar(); }, []);

  const estiloInput = { padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit' };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>De</label>
          <input type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} style={estiloInput} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Até</label>
          <input type="date" value={ate} min={de} onChange={(e) => setAte(e.target.value)} style={estiloInput} />
        </div>
        {lojas.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Loja</label>
            <select value={loja} onChange={(e) => setLoja(e.target.value)} style={estiloInput}>
              <option value="">Todas</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}
        <button onClick={buscar} disabled={carregando} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
          background: carregando ? '#cbd5e1' : COR, color: '#fff', border: 'none',
          borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: carregando ? 'default' : 'pointer',
        }}>
          {carregando ? <Loader2 size={16} className="spin" /> : <Search size={16} />} Buscar
        </button>
      </div>

      {erro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginBottom: 16, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14 }}>
          <AlertTriangle size={18} /> <span>{erro}</span>
        </div>
      )}

      {dados?.truncado && (
        <div style={{ padding: 12, marginBottom: 12, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13 }}>
          Muitos registros no período — mostrando os 2.000 mais recentes. Estreite o intervalo para ver o resto.
        </div>
      )}

      {corrigindo && (
        <div style={{ padding: 16, marginBottom: 16, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 14, color: '#92400e' }}>
          <strong>Corrigir a data do mapa de {corrigindo.loja}</strong> (hoje em {dataBr(corrigindo.data)}).
          <div style={{ margin: '4px 0 10px', fontSize: 13 }}>
            A loja será avisada, na tela de lançamento, de que o mapa foi lançado na data errada.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <input type="date" value={novaData} max={hojeIso()} onChange={(e) => setNovaData(e.target.value)} style={estiloInput} />
            <button onClick={salvarCorrecao} disabled={!novaData || salvando} style={{
              padding: '9px 16px', background: !novaData || salvando ? '#cbd5e1' : COR, color: '#fff', border: 'none',
              borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: !novaData || salvando ? 'default' : 'pointer',
            }}>{salvando ? 'Salvando…' : 'Salvar nova data'}</button>
            <button onClick={() => setCorrigindo(null)} disabled={salvando} style={{ padding: '9px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Cancelar</button>
          </div>
          {erroCorrecao && <div style={{ marginTop: 10, color: '#991b1b', fontWeight: 600 }}>{erroCorrecao}</div>}
        </div>
      )}

      {carregando && !dados ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
          <Loader2 size={18} className="spin" /> Carregando…
        </div>
      ) : !dados?.linhas?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: '#94a3b8', padding: 40 }}>
          <Inbox size={36} /> <span>Nenhum mapa lançado no período.</span>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1060 }}>
            <thead style={{ background: COR_FUNDO }}>
              <tr>
                <CabOrdenavel esquerda chave="data" ordem={ordem} onOrdenar={ordenar}>Data</CabOrdenavel>
                <CabOrdenavel esquerda chave="loja" ordem={ordem} onOrdenar={ordenar}>Loja</CabOrdenavel>
                <CabOrdenavel chave="totalDinheiroFita" ordem={ordem} onOrdenar={ordenar}>Dinheiro Fita</CabOrdenavel>
                <CabOrdenavel chave="totalValorBananas" ordem={ordem} onOrdenar={ordenar}>Depositado</CabOrdenavel>
                <CabOrdenavel chave="qtdBananas" ordem={ordem} onOrdenar={ordenar}>Nº Bananas</CabOrdenavel>
                <CabOrdenavel chave="quebraCaixa" ordem={ordem} onOrdenar={ordenar}>Quebra</CabOrdenavel>
                <CabOrdenavel chave="gprsMaquina" ordem={ordem} onOrdenar={ordenar}>GPRS Máquina</CabOrdenavel>
                <CabOrdenavel chave="diferencaGprs" ordem={ordem} onOrdenar={ordenar}>Dif. GPRS</CabOrdenavel>
                <CabOrdenavel chave="errosPosTotal" ordem={ordem} onOrdenar={ordenar}>Erros Pós</CabOrdenavel>
                <CabOrdenavel chave="nulosPosTotal" ordem={ordem} onOrdenar={ordenar}>Nulos Pós</CabOrdenavel>
                <CabOrdenavel chave="pctReferenteVenda" ordem={ordem} onOrdenar={ordenar}>% Venda</CabOrdenavel>
                <th style={thL}>Anexo</th>
                {podeCorrigirData && <th style={thL}>Data</th>}
              </tr>
            </thead>
            <tbody>
              {pag.itens.map((l, i) => {
                const chave = `${l.loja}|${l.data}`;
                const aberta = !!abertas[chave];
                const bn = bananas[chave];
                return (
                <React.Fragment key={`${l.loja}-${l.data}-${i}`}>
                <tr onClick={() => alternarBananas(l)} tabIndex={0} aria-expanded={aberta}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternarBananas(l); } }}
                  title="Clique para ver os números das bananas" style={{ cursor: 'pointer', background: aberta ? COR_FUNDO : undefined }}>
                  <td style={tdL}>
                    {aberta ? <ChevronDown size={14} style={{ verticalAlign: '-2px', marginRight: 4, color: COR }} /> : <ChevronRight size={14} style={{ verticalAlign: '-2px', marginRight: 4, color: '#94a3b8' }} />}
                    {dataBr(l.data)}
                    {l.dataOriginal && (
                      <div title={`Lançado pela loja em ${dataBr(l.dataOriginal)}; corrigido por ${l.dataCorrigidaPor || '—'}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, padding: '2px 7px', background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                        <CalendarClock size={11} /> corrigida (era {dataBr(l.dataOriginal)})
                      </div>
                    )}
                  </td>
                  <td style={tdL}><strong>{l.loja}</strong></td>
                  <td style={td}>{brl(l.totalDinheiroFita)}</td>
                  <td style={td}>{brl(l.totalValorBananas)}</td>
                  <td style={td}>{l.qtdBananas ?? '—'}</td>
                  <td style={td}>{celSinal(l.quebraCaixa)}</td>
                  <td style={td}>{brl(l.gprsMaquina)}</td>
                  <td style={td}>{celSinal(l.diferencaGprs)}</td>
                  <td style={td}>{brl(l.errosPosTotal)}</td>
                  <td style={td}>{brl(l.nulosPosTotal)}</td>
                  <td style={td}>{l.pctReferenteVenda === null || l.pctReferenteVenda === undefined ? '—' : `${l.pctReferenteVenda}%`}</td>
                  <td style={tdL}>
                    {l.anexoUrl ? (
                      <button onClick={(e) => { e.stopPropagation(); abrirArquivoComToken(`${API}/anexo?url=${encodeURIComponent(l.anexoUrl)}`, `mapa-${l.loja}-${l.data}`); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        <Paperclip size={13} /> Ver
                      </button>
                    ) : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                  {podeCorrigirData && (
                    <td style={tdL}>
                      <button onClick={(e) => { e.stopPropagation(); abrirCorrecao(l); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: '#fff', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        <CalendarClock size={13} /> Corrigir
                      </button>
                    </td>
                  )}
                </tr>
                {aberta && (
                  <tr>
                    <td colSpan={podeCorrigirData ? 13 : 12} style={{ padding: '12px 16px 16px 36px', background: COR_FUNDO, borderBottom: `1px solid ${COR_BORDA}`, textAlign: 'left', whiteSpace: 'normal' }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: COR_FORTE, marginBottom: 8 }}>
                        Bananas do PDV em {dataBr(l.data)} — {l.loja}
                      </div>
                      {bn?.parcial && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', marginBottom: 10, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13 }}>
                          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2 }} />
                          <span><strong>Dia ainda incompleto.</strong> O PDV publica as bananas ao longo do dia seguinte; esta lista pode ganhar números novos. Confira de novo depois.</span>
                        </div>
                      )}
                      {!bn || bn.carregando ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}><Loader2 size={14} className="spin" /> Buscando…</span>
                      ) : bn.erro ? (
                        <span style={{ color: '#991b1b', fontSize: 13 }}>{bn.erro}</span>
                      ) : bn.lista.length === 0 ? (
                        <span style={{ color: '#64748b', fontSize: 13 }}>Nenhuma banana registrada no PDV nesse dia.</span>
                      ) : (
                        <>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {bn.lista.map((c) => (
                              <span key={c} style={{ padding: '4px 10px', background: '#fff', color: COR_FORTE, border: `1px solid ${COR_BORDA}`, borderRadius: 6, fontWeight: 700, fontSize: 13, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.03em' }}>{c}</span>
                            ))}
                          </div>
                          <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
                            {bn.lista.length} no PDV · {l.qtdBananas ?? '—'} digitadas pela loja
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dados?.linhas?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13, color: '#334155' }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>Mostrando {pag.de}–{pag.ate} de {pag.total}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Linhas por página
              <select value={tamanho} onChange={(e) => trocarTamanho(Number(e.target.value))} style={{ ...estiloInput, padding: '6px 8px', fontSize: 13 }}>
                {TAMANHOS_PAGINA.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button type="button" onClick={() => setPagina(pag.pagina - 1)} disabled={pag.pagina <= 1}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#fff', color: pag.pagina <= 1 ? '#94a3b8' : COR_FORTE, border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: pag.pagina <= 1 ? 'default' : 'pointer' }}>
              <ChevronLeft size={14} /> Anterior
            </button>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>Página {pag.pagina} de {pag.totalPaginas}</span>
            <button type="button" onClick={() => setPagina(pag.pagina + 1)} disabled={pag.pagina >= pag.totalPaginas}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#fff', color: pag.pagina >= pag.totalPaginas ? '#94a3b8' : COR_FORTE, border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: pag.pagina >= pag.totalPaginas ? 'default' : 'pointer' }}>
              Próxima <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaCaixaConsulta;
